/**
 * Right-side drawer for task inspection (singleton — at most one mounted into
 * the Gantt container). Renders task or milestone form by `task.is_milestone`;
 * in create-mode the user picks via a radio toggle.
 *
 * Usage:
 *   mountTaskInspector(container);
 *   openTaskInspector({ task, resources, projects, milestones, onAction, onClose });
 *   refreshInspectorTask(updatedTask);
 *   getInspectorDiff();        // {field: newValue} of dirty fields, or null
 *   showInspectorError(msg);
 *   closeTaskInspector();
 */

import { attachInspectorResize } from '../utils/inspector-resize.js';
import { feverEmoji } from '../utils/format.js';
import { calcRatio, getZone, ZONE_LABELS } from '../utils/calibration-zones.js';

const STATUS_RU = {
  todo:        'todo',
  in_progress: 'in progress',
  done:        'done',
  cancelled:   'cancelled',
  blocked:     'blocked',
};

let _root = null;
let _state = resetState();

function resetState() {
  return {
    mode:       'closed',  // 'closed' | 'view' | 'create'
    taskId:     null,
    task:       null,
    resources:  [],
    projects:   [],
    milestones: [],
    onAction:   () => {},
    onClose:    () => {},
    extraOpen:  false,     // task-only; milestone form has no <details>
    errorMsg:   null,
  };
}

const isMs  = () => !!_state.task?.is_milestone;
const fbAuto = (v) => v !== false;
function milestonesForProject(projectId, excludeId) {
  // F5 fix (2026-05-10): после миграции 034 БД-триггер разрешает любую
  // задачу как parent (не только milestone). Список потенциальных
  // родителей расширен до ВСЕХ задач проекта. Сортировка: вехи
  // первыми (для совместимости с прежним UX) → потом обычные задачи
  // → cancelled в конце.
  return _state.milestones
    .filter(m => m.project_id === projectId && (excludeId == null || m.id !== excludeId))
    .sort((a, b) => {
      // milestones сначала
      if (a.is_milestone !== b.is_milestone) return a.is_milestone ? -1 : 1;
      // cancelled в конец
      const aDead = a.status === 'cancelled' ? 1 : 0;
      const bDead = b.status === 'cancelled' ? 1 : 0;
      if (aDead !== bDead) return aDead - bDead;
      return (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id - b.id);
    });
}

const FIELDS_NUMERIC = new Set([
  'owner_resource_id',
  'duration_days',
  'ruslan_minutes',
  'budget_rub',
  'actual_minutes_spent',
  'feeding_buffer_minutes',
  'parent_task_id',
  'work_pct',
]);
const FIELDS_BOOL   = new Set(['feeding_buffer_auto']);
const FIELDS_TIME   = new Set(['planned_start_time']);  // need HH:MM normalization
// Editable fields. `is_on_critical_chain` is intentionally NOT here: a DB
// trigger recomputes it from the dependency graph after every update, so any
// user toggle would silently bounce back. The flag is shown read-only.
const FIELDS_ALL    = [
  'name', 'owner_resource_id', 'duration_days', 'ruslan_minutes', 'budget_rub',
  'notes', 'context_flag',
  'planned_start_date', 'planned_start_time', 'actual_minutes_spent',
  'parent_task_id',
  'deadline', 'deadline_type', 'feeding_buffer_minutes', 'feeding_buffer_auto',
  'work_pct',
];

// Esc closes the inspector unless focus is in an editable field. Attached once
// at module-init so router-driven remounts don't accumulate listeners.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || _state.mode === 'closed') return;
  const t = document.activeElement;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
  closeTaskInspector();
});

function buildDOM() {
  const root = document.createElement('aside');
  root.className = 'task-inspector';
  root.hidden = true;
  root.innerHTML = `
    <header class="ti-header">
      <span class="ti-title">Задача</span>
      <button type="button" class="ti-close" title="Закрыть (Esc)" aria-label="Закрыть">✕</button>
    </header>
    <div class="ti-body"></div>
    <footer class="ti-footer"></footer>
  `;
  root.querySelector('.ti-close').addEventListener('click', closeTaskInspector);
  return root;
}

export function mountTaskInspector(container) {
  // The router resets main.innerHTML on every navigation, which detaches a
  // previously-mounted drawer. Rebuild if the singleton was orphaned.
  if (_root && !document.body.contains(_root)) {
    _root = null;
    _state = resetState();
  }
  if (_root) return _root;
  // Drawer is absolutely positioned inside the gantt container.
  if (getComputedStyle(container).position === 'static') {
    container.style.position = 'relative';
  }
  _root = buildDOM();
  container.appendChild(_root);
  attachInspectorResize(_root, { key: 'task', defaultWidth: 380 });   // UI-2: ширина панели
  return _root;
}

export function openTaskInspector({ task, resources, projects, milestones, onAction, onClose }) {
  if (!_root) return;
  _state = {
    ...resetState(),
    mode:       'view',
    taskId:     task.id,
    task,
    resources:  resources  ?? [],
    projects:   projects   ?? [],
    milestones: milestones ?? [],
    onAction:   onAction   ?? (() => {}),
    onClose:    onClose    ?? (() => {}),
  };
  render();
  _root.hidden = false;
  // Force reflow so the transition runs from translateX(100%) → 0.
  void _root.offsetWidth;
  _root.classList.add('ti-open');
}

/** Open the inspector in create mode with empty defaults. */
export function openCreateTaskInspector({ projects, resources, milestones, defaultProjectId, onAction, onClose }) {
  if (!_root) return;
  _state = {
    ...resetState(),
    mode:       'create',
    taskId:     null,
    task: {
      project_id:             defaultProjectId ?? null,
      name:                   '',
      is_milestone:           false,
      owner_resource_id:      null,
      duration_days:          1,
      ruslan_minutes:         null,
      budget_rub:             null,
      notes:                  '',
      context_flag:           null,
      parent_task_id:         null,
      deadline:               '',
      deadline_type:          'hard',
      feeding_buffer_minutes: null,
      feeding_buffer_auto:    true,
    },
    resources:  resources  ?? [],
    projects:   projects   ?? [],
    milestones: milestones ?? [],
    onAction:   onAction   ?? (() => {}),
    onClose:    onClose    ?? (() => {}),
  };
  render();
  _root.hidden = false;
  void _root.offsetWidth;
  _root.classList.add('ti-open');
  // Autofocus the first required field that is empty.
  setTimeout(() => {
    const projectField = _root.querySelector('select[name="project_id"]');
    const nameField    = _root.querySelector('input[name="name"]');
    const target = (projectField && !projectField.value) ? projectField : nameField;
    target?.focus();
  }, 30);
}

/** Build a validated payload for POST /api/tasks, or null on validation failure. */
export function getInspectorCreatePayload() {
  if (_state.mode !== 'create') return null;
  const form = _root?.querySelector('.ti-body form');
  if (!form) return null;

  const projectIdRaw = form.elements.project_id?.value;
  const name = (form.elements.name?.value ?? '').trim();
  if (!projectIdRaw) {
    showInspectorError('Выберите проект');
    return null;
  }
  if (!name) {
    showInspectorError('Имя не может быть пустым');
    return null;
  }

  const fields = readForm(form);

  if (isMs()) {
    if (!fields.deadline) {
      showInspectorError('Укажите дедлайн вехи');
      return null;
    }
    if (fields.deadline_type !== 'hard' && fields.deadline_type !== 'soft') {
      showInspectorError('Укажите тип дедлайна (hard/soft)');
      return null;
    }
    const payload = {
      project_id:    Number(projectIdRaw),
      name,
      is_milestone:  true,
      deadline:      fields.deadline,
      deadline_type: fields.deadline_type,
      feeding_buffer_auto: fbAuto(fields.feeding_buffer_auto),
    };
    if (fields.parent_task_id != null) payload.parent_task_id = fields.parent_task_id;
    if (fields.feeding_buffer_minutes != null) {
      payload.feeding_buffer_minutes = fields.feeding_buffer_minutes;
    }
    if (fields.notes != null) payload.notes = fields.notes;
    return payload;
  }

  // owner_resource_id is NOT NULL in DB.
  const ownerRaw = form.elements.owner_resource_id?.value;
  if (!ownerRaw) {
    showInspectorError('Выберите ресурс');
    return null;
  }
  const payload = {
    project_id: Number(projectIdRaw),
    name,
    owner_resource_id: Number(ownerRaw),
  };
  for (const k of ['duration_days', 'ruslan_minutes', 'budget_rub', 'notes', 'context_flag']) {
    const v = fields[k];
    if (v != null) payload[k] = v;
  }
  if (fields.parent_task_id != null) payload.parent_task_id = fields.parent_task_id;
  return payload;
}

export function closeTaskInspector() {
  if (!_root || _state.mode === 'closed') return;
  const cb = _state.onClose;
  _state = resetState();
  _root.classList.remove('ti-open');
  // Hide after the slide-out finishes; fallback timer covers reduced-motion users.
  let done = false;
  const finalize = () => {
    if (done) return;
    done = true;
    _root.hidden = true;
    cb();
  };
  _root.addEventListener('transitionend', finalize, { once: true });
  setTimeout(finalize, 250);
}

export function getCurrentTaskId() {
  return _state.taskId;
}

/** Update the inspector body if it's currently showing this task. */
export function refreshInspectorTask(updatedTask) {
  if (_state.mode !== 'view' || _state.taskId !== updatedTask.id) return;
  _state.task = { ..._state.task, ...updatedTask };
  _state.errorMsg = null;
  render();
}

/** Return a diff object {field: newValue} of dirty fields, or null if nothing changed. */
export function getInspectorDiff() {
  return collectDiff();
}

/** Show an inline error above the form (e.g. baseline-locked 409 details).
 *
 * F4 fix (2026-05-10): НЕ делаем полный render() — он перезаписывает
 * .ti-body innerHTML, и все введённые но ещё не сохранённые значения формы
 * теряются. Вместо этого обновляем DOM-блок .ti-error напрямую. При
 * следующем явном render() (открытие новой задачи / save success / close)
 * errorMsg корректно отрисуется через шаблон.
 */
export function showInspectorError(msg) {
  _state.errorMsg = msg;
  const body = _root?.querySelector('.ti-body');
  if (!body) return;
  let errEl = body.querySelector('.ti-error');
  if (msg) {
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.className = 'ti-error';
      body.insertBefore(errEl, body.firstChild);
    }
    errEl.textContent = msg;
  } else if (errEl) {
    errEl.remove();
  }
}

function render() {
  if (_state.mode === 'create') return renderCreate();
  return renderView();
}

function renderView() {
  return isMs() ? renderViewMilestone() : renderViewTask();
}

function renderViewTask() {
  const { task, resources, projects, errorMsg } = _state;
  const proj    = projects.find(p => p.id === task.project_id);
  const ownerId = task.owner_resource_id ?? '';
  const planTime = (task.planned_start_time ?? '').toString().slice(0, 5);

  _root.querySelector('.ti-title').textContent = `Задача #${task.id}`;

  _root.querySelector('.ti-body').innerHTML = `
    ${errorMsg ? `<div class="ti-error">${escapeHtml(errorMsg)}</div>` : ''}
    <form class="ti-form" novalidate>
      <div class="ti-field">
        <label class="ti-label" for="ti-name">Имя</label>
        <input id="ti-name" name="name" class="ti-input"
               autocomplete="off" data-1p-ignore
               type="text" value="${escapeAttr(task.name ?? '')}" required>
      </div>
      <div class="ti-row2">
        <div class="ti-field">
          <span class="ti-label">Проект</span>
          <span class="ti-value">${
            proj ? `${feverEmoji(proj.fever_zone)} ${escapeHtml(proj.name)}` : '—'
          }</span>
        </div>
        <div class="ti-field">
          <span class="ti-label">Статус</span>
          <span class="ti-value ti-status-${task.status}">${STATUS_RU[task.status] ?? task.status}</span>
        </div>
      </div>
      <div class="ti-field">
        <span class="ti-label">Тип записи</span>
        <span class="ti-value">
          Задача
          <button type="button" class="ti-btn-mini" data-action="convert-to-milestone"
                  title="Преобразовать в веху. ⚠ Длительность станет 0, понадобится дедлайн.">
            → Сделать вехой
          </button>
        </span>
      </div>
      <div class="ti-row2">
        <div class="ti-field">
          <label class="ti-label" for="ti-resource">Ресурс</label>
          <select id="ti-resource" name="owner_resource_id" class="ti-input">
            <option value="">— не выбран —</option>
            ${resources.map(r => `
              <option value="${r.id}"${r.id === ownerId ? ' selected' : ''}>${escapeHtml(r.name)}</option>
            `).join('')}
          </select>
        </div>
        <div class="ti-field">
          <span class="ti-label">CC</span>
          <span class="ti-value">${task.is_on_critical_chain ? '✓ critical chain' : '—'}</span>
        </div>
      </div>
      <div class="ti-row3">
        <div class="ti-field">
          <label class="ti-label" for="ti-days">Дни</label>
          <input id="ti-days" name="duration_days" class="ti-input"
                 type="number" min="0.25" step="0.25"
                 value="${task.duration_days ?? ''}">
        </div>
        <div class="ti-field">
          <label class="ti-label" for="ti-min">Минуты</label>
          <input id="ti-min" name="ruslan_minutes" class="ti-input"
                 type="number" min="0" step="1"
                 value="${task.ruslan_minutes ?? ''}">
        </div>
        <div class="ti-field">
          <label class="ti-label" for="ti-rub">Бюджет ₽</label>
          <input id="ti-rub" name="budget_rub" class="ti-input"
                 type="number" min="0" step="0.01"
                 value="${task.budget_rub ?? ''}">
        </div>
      </div>
      ${renderWorkPctBlock(task)}
      ${renderTimeOverrunBlock(task)}
      ${renderParentMilestoneField(task.parent_task_id, milestonesForProject(task.project_id, task.id))}
      <div class="ti-field">
        <label class="ti-label" for="ti-notes">Заметки</label>
        <textarea id="ti-notes" name="notes" class="ti-input ti-textarea"
                  rows="3">${escapeHtml(task.notes ?? '')}</textarea>
      </div>

      <details class="ti-extra"${_state.extraOpen ? ' open' : ''}>
        <summary>Дополнительно</summary>
        <div class="ti-extra-body">
          <div class="ti-field">
            <label class="ti-label" for="ti-context">Context flag</label>
            <input id="ti-context" name="context_flag" class="ti-input"
                   type="text" value="${escapeAttr(task.context_flag ?? '')}">
          </div>
          <div class="ti-row2">
            <div class="ti-field">
              <label class="ti-label" for="ti-pstart">Старт (план), дата</label>
              <input id="ti-pstart" name="planned_start_date" class="ti-input"
                     type="date" value="${escapeAttr(task.planned_start_date ?? '')}">
            </div>
            <div class="ti-field">
              <label class="ti-label" for="ti-ptime">Старт (план), время</label>
              <input id="ti-ptime" name="planned_start_time" class="ti-input"
                     type="time" value="${escapeAttr(planTime)}">
            </div>
          </div>
          ${task.status === 'done' ? `
            <div class="ti-field">
              <label class="ti-label" for="ti-actual">Факт. минут</label>
              <input id="ti-actual" name="actual_minutes_spent" class="ti-input"
                     type="number" min="0" step="1"
                     value="${task.actual_minutes_spent ?? ''}">
            </div>` : ''}
        </div>
      </details>
    </form>
  `;

  wireViewForm();
  renderFooter(renderActionsTask(task));
}

function renderViewMilestone() {
  const { task, projects, errorMsg } = _state;
  const proj = projects.find(p => p.id === task.project_id);
  const auto = fbAuto(task.feeding_buffer_auto);
  // Веха: work_pct (миграция 016) — auto-AVG детских work_pct через триггер.
  // NULL = ни один ребёнок не оценён → показываем «—» вместо «0.0 %».
  const workPct = task.work_pct != null ? Number(task.work_pct) : null;
  const overdueMark = task.deadline_type === 'hard' ? '🔴 hard' : '🟡 soft';

  _root.querySelector('.ti-title').textContent = `Веха #${task.id}`;

  _root.querySelector('.ti-body').innerHTML = `
    ${errorMsg ? `<div class="ti-error">${escapeHtml(errorMsg)}</div>` : ''}
    <form class="ti-form" novalidate>
      <div class="ti-field">
        <label class="ti-label" for="ti-name">Имя</label>
        <input id="ti-name" name="name" class="ti-input"
               autocomplete="off" data-1p-ignore
               type="text" value="${escapeAttr(task.name ?? '')}" required>
      </div>
      <div class="ti-row2">
        <div class="ti-field">
          <span class="ti-label">Проект</span>
          <span class="ti-value">${
            proj ? `${feverEmoji(proj.fever_zone)} ${escapeHtml(proj.name)}` : '—'
          }</span>
        </div>
        <div class="ti-field">
          <span class="ti-label">Тип записи</span>
          <span class="ti-value">
            ◆ ${overdueMark}
            <button type="button" class="ti-btn-mini" data-action="convert-to-task"
                    title="Преобразовать в обычную задачу. ⚠ Может потребоваться задать длительность.">
              → Сделать задачей
            </button>
          </span>
        </div>
      </div>
      ${renderParentMilestoneField(task.parent_task_id, milestonesForProject(task.project_id, task.id))}
      <div class="ti-row2">
        <div class="ti-field">
          <label class="ti-label" for="ti-deadline">Дедлайн *</label>
          <input id="ti-deadline" name="deadline" class="ti-input"
                 type="date" value="${escapeAttr(task.deadline ?? '')}" required>
        </div>
        <div class="ti-field">
          <label class="ti-label" for="ti-dtype">Тип дедлайна *</label>
          <select id="ti-dtype" name="deadline_type" class="ti-input" required>
            <option value="hard"${task.deadline_type === 'hard' ? ' selected' : ''}>hard (жёсткий)</option>
            <option value="soft"${task.deadline_type === 'soft' ? ' selected' : ''}>soft (мягкий)</option>
          </select>
        </div>
      </div>
      <div class="ti-row2">
        <div class="ti-field">
          <label class="ti-label">
            <input type="checkbox" name="feeding_buffer_auto"${auto ? ' checked' : ''}>
            Авто feeding buffer (30% от CC)
          </label>
        </div>
        <div class="ti-field">
          <label class="ti-label" for="ti-fbuf">Буфер слияния, мин</label>
          <input id="ti-fbuf" name="feeding_buffer_minutes" class="ti-input"
                 type="number" min="0" step="1"
                 value="${task.feeding_buffer_minutes ?? 0}"
                 ${auto ? 'disabled' : ''}
                 title="${auto ? 'Снимите автомат, чтобы задать вручную' : ''}">
        </div>
      </div>
      <div class="ti-field">
        <span class="ti-label">Прогресс (AVG объёма дочерних задач)</span>
        <span class="ti-value">${workPct != null ? workPct.toFixed(1) + ' %' : '— (нет оценок у задач)'}</span>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="ti-notes">Заметки</label>
        <textarea id="ti-notes" name="notes" class="ti-input ti-textarea"
                  rows="3">${escapeHtml(task.notes ?? '')}</textarea>
      </div>
    </form>
  `;

  wireViewForm();
  wireFeedingBufferAutoToggle({ syncSaveBtn: true });
  renderFooter(renderActionsMilestone());
}

function wireFeedingBufferAutoToggle({ syncSaveBtn }) {
  const autoCb = _root.querySelector('input[name="feeding_buffer_auto"]');
  const fbufIn = _root.querySelector('input[name="feeding_buffer_minutes"]');
  if (!autoCb || !fbufIn) return;
  autoCb.addEventListener('change', () => {
    fbufIn.disabled = autoCb.checked;
    fbufIn.title = autoCb.checked ? 'Снимите автомат, чтобы задать вручную' : '';
    fbufIn.placeholder = autoCb.checked ? 'считается автоматически' : 'введите минуты';
    if (syncSaveBtn) updateSaveButtonState();
  });
}

// Слайдер «Сделано» (work_pct) — субъективная оценка объёма выполненной работы
// 0..100% (миграция 016). Центральный сигнал прогресса в новой UX-модели:
// колонка «%» в Gantt и эта секция инспектора — это work_pct, а не «съеденное
// время». NULL означает «явно не оценивалось» — слайдер показывает 0, но input
// value остаётся пустой строкой → readForm вернёт null.
function renderWorkPctBlock(task) {
  const wp = task.work_pct;
  const wpVal = wp != null ? Number(wp) : null;
  const sliderVal = wpVal ?? 0;
  const display = wpVal != null ? `${wpVal.toFixed(0)} %` : '— не оценивалось';
  return `
    <div class="ti-field ti-workpct">
      <label class="ti-label" for="ti-workpct">Сделано (объём работы)</label>
      <div class="ti-workpct-row">
        <input id="ti-workpct" name="work_pct" class="ti-workpct-slider"
               type="range" min="0" max="100" step="5"
               value="${sliderVal}"
               data-was-null="${wpVal == null ? '1' : '0'}">
        <span class="ti-workpct-value" data-display>${display}</span>
      </div>
      <div class="ti-workpct-quick" role="group" aria-label="Быстрая установка">
        ${[0, 25, 50, 75, 100].map(v =>
          `<button type="button" class="ti-btn ti-btn-quick" data-workpct="${v}">${v}%</button>`
        ).join('')}
      </div>
    </div>
  `;
}

// План/Факт по времени для завершённой задачи. Пороги зон вынесены в
// utils/calibration-zones.js — единый источник для inspector / страницы
// /calibration / виджета на Dashboard. Не показываем для незавершённых.
//
// Если у задачи есть first_baseline_ruslan_minutes (миграция 026) — берём
// первую baseline-оценку, не текущую baseline_ruslan_minutes (которая
// может быть rebaseline'нута и исказит сигнал калибрации).
function renderTimeOverrunBlock(task) {
  if (task.status !== 'done') return '';
  const plan = Number(
    task.first_baseline_ruslan_minutes ?? task.ruslan_minutes ?? 0
  );
  const fact = Number(task.actual_minutes_spent ?? 0);
  const ratio = calcRatio(plan, fact);
  if (ratio == null) return '';
  const zone = getZone(ratio);
  return `
    <div class="ti-field ti-overrun ti-overrun-${zone}">
      <span class="ti-label">План / Факт</span>
      <div class="ti-overrun-grid">
        <div><span class="ti-overrun-key">План:</span> ${plan} мин</div>
        <div><span class="ti-overrun-key">Факт:</span> ${fact} мин (${ratio.toFixed(0)}% от плана)</div>
        <div class="ti-overrun-label">${ZONE_LABELS[zone]}</div>
      </div>
    </div>
  `;
}

function renderParentMilestoneField(currentValue, projMilestones) {
  // F5 fix (2026-05-10): label переименован «Родительская веха» →
  // «Родительская задача». Dropdown показывает ЛЮБЫЕ задачи проекта
  // (не только milestones). DB-триггер ccpm.validate_milestone_parent
  // (миграция 034) теперь разрешает не-milestone parent при условии
  // 2-level depth ограничения.
  // Префикс ◆ для milestones чтобы визуально различать.
  const opts = projMilestones
    .map(m => {
      const prefix = m.is_milestone ? '◆ ' : '';
      const suffix = m.status === 'cancelled' ? ' (отменена)' : '';
      const label = `${prefix}${m.name}${suffix}`;
      return `<option value="${m.id}"${m.id === currentValue ? ' selected' : ''}>${escapeHtml(label)}</option>`;
    })
    .join('');
  return `
    <div class="ti-field">
      <label class="ti-label" for="ti-parent">Родительская задача</label>
      <select id="ti-parent" name="parent_task_id" class="ti-input">
        <option value=""${currentValue == null ? ' selected' : ''}>— под проектом —</option>
        ${opts}
      </select>
    </div>
  `;
}

function wireViewForm() {
  const form = _root.querySelector('.ti-body form');
  const details = _root.querySelector('.ti-extra');
  if (details) details.addEventListener('toggle', () => { _state.extraOpen = details.open; });
  form.addEventListener('input',  updateSaveButtonState);
  form.addEventListener('change', updateSaveButtonState);

  // F20 (2026-05-10): handlers для inline-кнопок «Сделать вехой / задачей».
  // Делегируем в _state.onAction (gantt.js handleInspectorAction).
  form.querySelectorAll('button[data-action="convert-to-task"], button[data-action="convert-to-milestone"]')
    .forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        _state.onAction(btn.dataset.action);
      });
    });

  // work_pct: слайдер + быстрые кнопки + live-обновление displayed value.
  const slider = form.querySelector('input[name="work_pct"]');
  if (slider) {
    const display = form.querySelector('.ti-workpct-value');
    const updateDisplay = () => {
      if (display) display.textContent = `${slider.value} %`;
      slider.dataset.wasNull = '0';  // юзер коснулся → больше не NULL
    };
    slider.addEventListener('input', updateDisplay);
    form.querySelectorAll('.ti-btn-quick').forEach(btn => {
      btn.addEventListener('click', () => {
        slider.value = btn.dataset.workpct;
        updateDisplay();
        // Принудительно дёргаем 'input' на форме, чтобы updateSaveButtonState сработал.
        slider.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
  }
}

function renderFooter(html) {
  const footer = _root.querySelector('.ti-footer');
  footer.innerHTML = html;
  footer.querySelectorAll('button[data-action]').forEach(btn => {
    btn.addEventListener('click', () => _state.onAction(btn.dataset.action));
  });
  updateSaveButtonState();
}

function readForm(form) {
  const out = {};
  const get = (name) => form.elements.namedItem(name);
  for (const name of FIELDS_ALL) {
    const el = get(name);
    if (!el) continue;
    // Range-input для work_pct всегда имеет value (≥ 0), но если юзер не
    // тронул его, оставляем null — иначе сохраним 0 для всех неоценённых
    // задач при первом же save любого другого поля.
    if (name === 'work_pct' && el.dataset && el.dataset.wasNull === '1') {
      out[name] = null;
      continue;
    }
    if (FIELDS_BOOL.has(name)) {
      out[name] = !!el.checked;
    } else if (FIELDS_NUMERIC.has(name)) {
      out[name] = el.value === '' ? null : Number(el.value);
    } else if (FIELDS_TIME.has(name)) {
      out[name] = el.value === '' ? null : el.value.slice(0, 5);
    } else {
      const v = (el.value ?? '').trim();
      out[name] = v === '' ? null : v;
    }
  }
  return out;
}

function diffField(name, baseline, current) {
  const b = baseline?.[name];
  const c = current[name];
  if (FIELDS_BOOL.has(name)) {
    // baseline may be undefined for new milestone fields on legacy task rows.
    const bb = b == null ? true : !!b;
    return bb !== !!c;
  }
  if (FIELDS_NUMERIC.has(name)) {
    const bn = b == null || b === '' ? null : Number(b);
    const cn = c == null || c === '' ? null : Number(c);
    return bn !== cn;
  }
  if (FIELDS_TIME.has(name)) {
    const norm = (s) => s == null || s === '' ? null : String(s).slice(0, 5);
    return norm(b) !== norm(c);
  }
  const bs = b == null || b === '' ? null : String(b);
  const cs = c == null || c === '' ? null : String(c);
  return bs !== cs;
}

function collectDiff() {
  const form = _root?.querySelector('.ti-body form');
  if (!form) return null;
  const baseline = _state.task;
  const current  = readForm(form);
  const diff = {};
  for (const k of Object.keys(current)) {
    if (diffField(k, baseline, current)) diff[k] = current[k];
  }
  return Object.keys(diff).length ? diff : null;
}

function updateSaveButtonState() {
  const saveBtn = _root.querySelector('.ti-footer [data-action="save"]');
  if (!saveBtn) return;
  const dirty = !!collectDiff();
  saveBtn.disabled = !dirty;
}

function renderCreate() {
  const { errorMsg } = _state;
  _root.querySelector('.ti-title').textContent = isMs() ? 'Новая веха' : 'Новая задача';

  const radio = renderRecordTypeToggle();
  const formInner = isMs() ? renderCreateMilestoneFields() : renderCreateTaskFields();

  _root.querySelector('.ti-body').innerHTML = `
    ${errorMsg ? `<div class="ti-error">${escapeHtml(errorMsg)}</div>` : ''}
    <form class="ti-form" novalidate>
      ${radio}
      ${formInner}
    </form>
  `;

  wireCreateForm();

  renderFooter(`
    <button type="button" data-action="create" class="ti-btn ti-btn-primary">✓ Создать</button>
    <button type="button" data-action="cancel" class="ti-btn">Отмена</button>
  `);
}

function renderRecordTypeToggle() {
  const ms = isMs();
  return `
    <fieldset class="ti-field ti-record-type">
      <legend class="ti-label">Тип записи</legend>
      <label class="ti-radio">
        <input type="radio" name="record_type" value="task"${!ms ? ' checked' : ''}>
        Задача
      </label>
      <label class="ti-radio">
        <input type="radio" name="record_type" value="milestone"${ms ? ' checked' : ''}>
        ◆ Веха
      </label>
    </fieldset>
  `;
}

function renderCreateTaskFields() {
  const { task, resources, projects } = _state;
  const liveProjects = projects.filter(p => ['active', 'queued', 'paused'].includes(p.status));

  return `
    <div class="ti-field">
      <label class="ti-label" for="ti-project">Проект *</label>
      <select id="ti-project" name="project_id" class="ti-input" required>
        <option value="">— выберите проект —</option>
        ${liveProjects.map(p => `
          <option value="${p.id}"${p.id === task.project_id ? ' selected' : ''}>${escapeHtml(p.name)}</option>
        `).join('')}
      </select>
    </div>
    <div class="ti-field">
      <label class="ti-label" for="ti-name">Имя *</label>
      <input id="ti-name" name="name" class="ti-input"
             type="text" value="${escapeAttr(task.name ?? '')}" required>
    </div>
    <div class="ti-field">
      <label class="ti-label" for="ti-resource">Ресурс *</label>
      <select id="ti-resource" name="owner_resource_id" class="ti-input" required>
        <option value="">— выберите —</option>
        ${resources.map(r => `
          <option value="${r.id}"${r.id === task.owner_resource_id ? ' selected' : ''}>${escapeHtml(r.name)}</option>
        `).join('')}
      </select>
    </div>
    <div class="ti-row3">
      <div class="ti-field">
        <label class="ti-label" for="ti-days">Дни</label>
        <input id="ti-days" name="duration_days" class="ti-input"
               type="number" min="0.25" step="0.25"
               value="${task.duration_days ?? 1}">
      </div>
      <div class="ti-field">
        <label class="ti-label" for="ti-min">Минуты</label>
        <input id="ti-min" name="ruslan_minutes" class="ti-input"
               type="number" min="0" step="1"
               value="${task.ruslan_minutes ?? ''}">
      </div>
      <div class="ti-field">
        <label class="ti-label" for="ti-rub">Бюджет ₽</label>
        <input id="ti-rub" name="budget_rub" class="ti-input"
               type="number" min="0" step="0.01"
               value="${task.budget_rub ?? ''}">
      </div>
    </div>
    ${renderParentMilestoneField(task.parent_task_id, milestonesForProject(task.project_id))}
    <div class="ti-field">
      <label class="ti-label" for="ti-notes">Заметки</label>
      <textarea id="ti-notes" name="notes" class="ti-input ti-textarea"
                rows="3">${escapeHtml(task.notes ?? '')}</textarea>
    </div>
    <details class="ti-extra"${_state.extraOpen ? ' open' : ''}>
      <summary>Дополнительно</summary>
      <div class="ti-extra-body">
        <div class="ti-field">
          <label class="ti-label" for="ti-context">Context flag</label>
          <input id="ti-context" name="context_flag" class="ti-input"
                 type="text" value="${escapeAttr(task.context_flag ?? '')}">
        </div>
        <p class="ti-hint">Запланированный старт и время задаются после создания —
          откройте созданную задачу и заполните блок «Дополнительно».</p>
      </div>
    </details>
  `;
}

function renderCreateMilestoneFields() {
  const { task, projects } = _state;
  const liveProjects = projects.filter(p => ['active', 'queued', 'paused'].includes(p.status));
  const auto = fbAuto(task.feeding_buffer_auto);

  return `
    <div class="ti-field">
      <label class="ti-label" for="ti-project">Проект *</label>
      <select id="ti-project" name="project_id" class="ti-input" required>
        <option value="">— выберите проект —</option>
        ${liveProjects.map(p => `
          <option value="${p.id}"${p.id === task.project_id ? ' selected' : ''}>${escapeHtml(p.name)}</option>
        `).join('')}
      </select>
    </div>
    <div class="ti-field">
      <label class="ti-label" for="ti-name">Имя вехи *</label>
      <input id="ti-name" name="name" class="ti-input"
             type="text" value="${escapeAttr(task.name ?? '')}" required
             placeholder="Например: Документ готов">
    </div>
    ${renderParentMilestoneField(task.parent_task_id, milestonesForProject(task.project_id))}
    <div class="ti-row2">
      <div class="ti-field">
        <label class="ti-label" for="ti-deadline">Дедлайн *</label>
        <input id="ti-deadline" name="deadline" class="ti-input"
               type="date" value="${escapeAttr(task.deadline ?? '')}" required>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="ti-dtype">Тип дедлайна *</label>
        <select id="ti-dtype" name="deadline_type" class="ti-input" required>
          <option value="hard"${task.deadline_type !== 'soft' ? ' selected' : ''}>hard (жёсткий)</option>
          <option value="soft"${task.deadline_type === 'soft' ? ' selected' : ''}>soft (мягкий)</option>
        </select>
      </div>
    </div>
    <div class="ti-row2">
      <div class="ti-field">
        <label class="ti-label">
          <input type="checkbox" name="feeding_buffer_auto"${auto ? ' checked' : ''}>
          Авто feeding buffer (30% от CC)
        </label>
      </div>
      <div class="ti-field">
        <label class="ti-label" for="ti-fbuf">Буфер слияния, мин</label>
        <input id="ti-fbuf" name="feeding_buffer_minutes" class="ti-input"
               type="number" min="0" step="1"
               value="${task.feeding_buffer_minutes ?? ''}"
               ${auto ? 'disabled' : ''}
               title="${auto ? 'Снимите автомат, чтобы задать вручную' : ''}"
               placeholder="${auto ? 'считается автоматически' : 'введите минуты'}">
      </div>
    </div>
    <div class="ti-field">
      <label class="ti-label" for="ti-notes">Заметки</label>
      <textarea id="ti-notes" name="notes" class="ti-input ti-textarea"
                rows="3">${escapeHtml(task.notes ?? '')}</textarea>
    </div>
    <p class="ti-hint">Веха = ромбик на дате дедлайна. Перед ним — фиолетовый
      штрихованный «буфер слияния» (защищает дедлайн от опозданий веток).</p>
  `;
}

function wireCreateForm() {
  const form = _root.querySelector('.ti-body form');

  // Snapshot user-entered values before re-render so switching task↔milestone
  // (or project, which rebuilds the parent-milestone <select>) doesn't clobber
  // the typed name.
  form.querySelectorAll('input[name="record_type"]').forEach(r =>
    r.addEventListener('change', (e) => {
      snapshotCreateForm();
      _state.task.is_milestone = (e.target.value === 'milestone');
      rerenderAndRefocus(`input[name="record_type"][value="${e.target.value}"]`);
    }));

  form.querySelector('select[name="project_id"]')?.addEventListener('change', () => {
    snapshotCreateForm();
    rerenderAndRefocus('select[name="project_id"]');
  });

  wireFeedingBufferAutoToggle({ syncSaveBtn: false });

  const details = _root.querySelector('.ti-extra');
  if (details) details.addEventListener('toggle', () => { _state.extraOpen = details.open; });
}

function rerenderAndRefocus(selector) {
  render();
  setTimeout(() => _root.querySelector(selector)?.focus(), 0);
}

/** Push current form values into _state.task so a re-render preserves them. */
function snapshotCreateForm() {
  const form = _root?.querySelector('.ti-body form');
  if (!form) return;
  const projectIdRaw = form.elements.project_id?.value;
  if (projectIdRaw !== undefined) {
    _state.task.project_id = projectIdRaw === '' ? null : Number(projectIdRaw);
  }
  for (const [k, v] of Object.entries(readForm(form))) _state.task[k] = v;
}

function renderActionsTask(task) {
  const status = task.status;
  const buttons = [saveBtnHtml()];
  if (status !== 'done' && status !== 'cancelled') {
    buttons.push(btn('complete', '✓ Завершить', 'ti-btn-primary'));
  }
  if (status === 'todo') {
    buttons.push(btn('start', '▶ Начать'));
  }
  if (status === 'in_progress') {
    buttons.push(btn('block', '⛔ Заблокировать'));
  }
  if (status === 'blocked') {
    buttons.push(btn('unblock', '↺ Разблокировать'));
  }
  if (status !== 'cancelled') {
    buttons.push(btn('delete', '🗑 Удалить', 'ti-btn-danger'));
  }
  return buttons.join('');
}

function renderActionsMilestone() {
  // Milestones have no Start/Complete/Block flow — status is always 'todo' until cancelled.
  return saveBtnHtml() + btn('delete', '🗑 Удалить', 'ti-btn-danger');
}

const saveBtnHtml = () =>
  `<button type="button" data-action="save" class="ti-btn ti-btn-save" disabled>💾 Сохранить</button>`;

function btn(action, label, extraClass = '') {
  return `<button type="button" data-action="${action}" class="ti-btn ${extraClass}">${escapeHtml(label)}</button>`;
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function escapeAttr(s) {
  return escapeHtml(s);
}
